/** Stable server error contract. Individual permissions are deliberately separate. */
export function getOrganizationAccessError(error: unknown): { state: string } | null {
  if (typeof error !== "object" || error === null || !("data" in error)) return null;
  const data = error.data;
  if (typeof data !== "object" || data === null || !("code" in data) ||
      data.code !== "ORGANIZATION_ACCESS_REQUIRED") return null;
  return { state: "state" in data && typeof data.state === "string" ? data.state : "unavailable" };
}

/**
 * Company-level access copy. It describes the organization's entitlement only; a person's own role
 * limits (for example a read-only reviewer) use separate permission messages.
 */
export function getOrganizationAccessTitle(state: string) {
  switch (state) {
    case "unpaid_setup": return "Subscription needed";
    case "expired": return "Company access has ended";
    case "suspended": return "Company access suspended";
    default: return "Company access needed";
  }
}

export function getOrganizationAccessMessage(state: string) {
  switch (state) {
    case "unpaid_setup": return "Your company needs an active subscription before anyone can create projects, upload documents, or process work.";
    case "expired": return "Your company’s access has ended, so creating, editing, and processing work are paused.";
    case "suspended": return "Your company’s access is suspended. Contact support to restore it.";
    default: return "Your company can’t create, edit, or process work right now.";
  }
}

let current: { state: string } | null = null;
let scope: string | null = null;
let returnFocusTarget: HTMLElement | null = null;
export const getAccessErrorReturnFocus = () => returnFocusTarget;
function activeElement() {
  return typeof document !== "undefined" && document.activeElement instanceof HTMLElement ? document.activeElement : null;
}
let generation = 0;
const listeners = new Set<() => void>();
const reported = new WeakSet<object>();
function emit() { for (const listener of listeners) listener(); }
export const subscribeToAccessErrors = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
export const getAccessErrorSnapshot = () => current;
export function dismissAccessError() { current = null; emit(); }
export function setAccessErrorScope(next: string | null) {
  if (scope === next) return;
  scope = next;
  generation++;
  dismissAccessError();
}

/** One presentation even when a rejection passes through several catch handlers. */
export function handleOrganizationAccessError(error: unknown, focusTarget = activeElement()) {
  const access = getOrganizationAccessError(error);
  if (!access) return false;
  if (!reported.has(error as object)) {
    reported.add(error as object);
    if (!current) { returnFocusTarget = focusTarget; current = access; emit(); }
  }
  return true;
}

/**
 * Open the shared access dialog before a restricted action is attempted, when the client already
 * knows (from the same effective-access policy) that the server would refuse it.
 */
export function showOrganizationAccessRequired(state: string, focusTarget = activeElement()) {
  if (current) return;
  returnFocusTarget = focusTarget;
  current = { state };
  emit();
}

/** Observe failures centrally, but preserve rejection and optimistic rollback semantics. */
export async function observeAccessFailure<T>(promise: Promise<T>): Promise<T> {
  const startedIn = generation;
  const focusTarget = activeElement();
  try { return await promise; }
  catch (error) {
    if (startedIn === generation) handleOrganizationAccessError(error, focusTarget);
    else if (getOrganizationAccessError(error)) reported.add(error as object);
    throw error;
  }
}
